<script setup lang="ts">
import { storeToRefs } from "pinia";
import { ref, computed } from "vue";
import "@maersk-global/mds-components-core-tag";
import { InputTypeCode } from "@/enum/search_enum";
import { regularExpressions } from "@/utils/regular_expression";
import { useExportSearchIdsStore } from "@/store/export/export_id_store";
import { useTerminalConfigurationStore } from "@/store/common/terminal_configuration_store";
import { useGlobalStore } from "@/store/global";
import { useExportBookingDetailStore } from "@/store/export/export_asset_store";

const globalStore = useGlobalStore();
const searchStore = useExportSearchIdsStore();
const terminalConfigurationStore = useTerminalConfigurationStore();
const exportBooking = useExportBookingDetailStore();

const backSpaceCount = ref<number>(0);
const inputTextValue = ref<string>("");
const { getExportSearchIds } = storeToRefs(searchStore);
const searchIdLimit = computed((): number => terminalConfigurationStore.searchIdLimit);
const noResult = computed((): string[] => exportBooking.noResult);

const labels = {
  containerSearchHeading: globalStore.sitecoreData.headings.search_heading_export,
  searchId: globalStore.sitecoreData.headings.search_ids,
  invalidId: globalStore.sitecoreData.search.invalid_id,
  remainingEntries: globalStore.sitecoreData.search.entries_remaining
};

const onInputChange = (event: any): void => {
  // event is of type InputEvent
  const inputValue = event.target.value;
  if (regularExpressions.separatorRegex.test(event.data)) {
    const list: string[] = inputValue.split(regularExpressions.separatorRegex);
    list.forEach((element: string): void => {
      searchStore.addExportSearchId(element);
    });
    inputTextValue.value = "";
  } else if (event.inputType === InputTypeCode.PASTE) {
    const list = inputValue?.split(regularExpressions.separatorRegex);
    list.forEach((element: string): void => {
      searchStore.addExportSearchId(element);
    });
    inputTextValue.value = "";
  } else {
    inputTextValue.value = inputValue;
  }
};

// Coding

const getInputPlaceHolder = computed((): string => {
  return labels.searchId;
});

const keyPressHandler = (event: any): void => {
  // event is of type InputEvent
  if (event.code === InputTypeCode.ENTER) {
    onEnterHandler(event);
  }
  if (event.target.value === "" && event.code === InputTypeCode.BACKSPACE) {
    backSpaceCount.value++;
    if (backSpaceCount.value > 1) {
      getExportSearchIds.value.pop();
    }
  } else {
    backSpaceCount.value = 0;
  }
};

const onBlurHandler = (event: InputEvent) => {
  onEnterHandler(event);
};

const onEnterHandler = (event: any): void => {
  // event is of type InputEvent
  const inputValue = event.target.value.trim().toString();
  if (inputValue === "") return;
  searchStore.addExportSearchId(inputValue);
  inputTextValue.value = "";
};

const removeIds = (id: string): void => {
  searchStore.removeSearchId(id);
};
const getTagAppearance = (id: string) => {
  return noResult.value.includes(id) ? "error" : "neutral";
};
</script>

<!-- UI elements -->
<template>
  <div>
    <div class="export_booking_heading">
      {{ labels.containerSearchHeading }}
    </div>
    <div class="search-view__input-container">
      <div class="search-view__grid-container" data-test="search-view__grid-container">
        <mc-tag
          v-for="(id, index) in getExportSearchIds"
          :key="index"
          :label.prop="id"
          withaction="true"
          :appearance="getTagAppearance(id)"
          fit="medium"
          icon="container"
          class="search-view__tag"
          @dismiss="removeIds(id)"
        />
        <mc-input
          v-model="inputTextValue"
          data-test="search-input"
          :placeholder="getInputPlaceHolder"
          hiddenlabel
          name="search"
          type="text"
          style="flex: 1 1 20%"
          autocomplete="off"
          icon="magnifying-glass"
          @input="onInputChange"
          @keyup="keyPressHandler"
          @keydown.tab="onBlurHandler"
          @blur="onBlurHandler"
        />
      </div>
    </div>
    <div class="max-limit">
      <span
        v-if="getExportSearchIds.length > 0"
        class="max-limit-message app__placeholder"
        data-test="remaining-entries-count"
      >
        <span class="mds-text--small-bold mds-secondary__text-color">
          {{ searchIdLimit - getExportSearchIds.length }}
        </span>
        ({{ labels.remainingEntries }})
      </span>
      <span v-else class="max-limit-message app__placeholder">
        <span class="mds-text--small-bold mds-secondary__text-color"> {{ searchIdLimit }} </span>
        ({{ labels.remainingEntries }})
      </span>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.export_booking_heading {
  margin-top: 20px;
  font-size: 16px;
}
</style>
